import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { createMask } from '@ngneat/input-mask';
import { saveAs } from 'file-saver';
import { Subscription, from } from 'rxjs';
import { ContractForm } from 'src/app/classes/form-groups/contract-form';
import { Contract } from 'src/app/classes/models';
import { File as FileModel } from 'src/app/classes/models/file';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { ContractFileUploadModalComponent } from 'src/app/modals/contract-file-upload-modal/contract-file-upload-modal.component';
import { ContractService } from 'src/app/services/contract.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-contract-add-contract',
  templateUrl: './contract-add-contract.component.html',
  styleUrls: ['./contract-add-contract.component.less'],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContractAddContractComponent implements OnInit, OnDestroy {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private ContractService: ContractService,
    private ToastService: ToastService,
    private SharedDataService: SharedDataService,
    private DatePipe: DatePipe,
    private UtilityService: UtilityService,
    private ModalService: NgbModal,
    private Route: ActivatedRoute
  ) {
    this.ContractService.getContractFrequencies().subscribe({
      next: (frequencies) => {
        if (!frequencies || frequencies.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Contract Frequencies Found.',
            { returnError: true }
          );
        }

        this.contractFrequencies = frequencies;
      }
    });

    this.contractForm.valueChanges.subscribe({
      next: (changes: Contract) => {
        if (changes) {
          this.SharedDataService.sendEvent(
            'UpdateContract',
            new Contract(changes)
          );
        }
      }
    });

    this.sharedDataServiceSubscription =
      this.SharedDataService.listenForEvent().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'SetContractId') {
            return this.ContractService.getContract(
              this.UtilityService.stringifyObjectsInQueryObject({
                id: data,
                File: {}
              })
            ).subscribe({
              next: (Contract: Contract) => {
                this.Contract = Contract;
                if (Contract) {
                  const {
                    startDate,
                    expirationDate,
                    renewalDate,
                    cancellationReminderDate,
                    renewalTerm,
                    dateToNotify,
                    Files
                  } = this.Contract;
                  const formattedContract = {
                    ...this.Contract,
                    startDate: this.formatDate(startDate),
                    expirationDate: this.formatDate(expirationDate),
                    cancellationReminderDate: this.formatDate(
                      cancellationReminderDate
                    ),
                    renewalDate: this.formatDate(renewalDate),
                    dateToNotify: this.formatDate(dateToNotify)
                  };
                  this.contractForm.patchValue(formattedContract);

                  if (Files && Files.length > 0) {
                    this.Files = Files;
                  }
                  this._cdr.markForCheck();
                }
              }
            });
          }

          if (eventName === 'isFormValid') {
            const errors = this.UtilityService.generateValidationErrorsObject(
              this.contractForm
            );
            if (errors) {
              throw this.ToastService.addErrorToast(
                'Error',
                'All required fields must be completed to proceed.',
                { returnError: true }
              );
            } else {
              return this.SharedDataService.sendEvent('formIsValid');
            }
          }
        }
      });

    this.SharedDataService.sendEvent('GetContractId');
  }

  contractForm: FormGroup = new ContractForm();

  contractFrequencies!: { id: number; periodName: string }[];
  deliveryMethods: string[] = [
    'Certified Mail',
    'Standard Mail',
    'E-Mail',
    'Fax'
  ];
  Contract?: Contract;
  Files: FileModel[] = [];
  sharedDataServiceSubscription!: Subscription;

  currencyMask = createMask({
    alias: 'numeric',
    groupSeparator: ',',
    digits: 2,
    digitsOptional: true,
    placeholder: '',
    rightAlign: false
  });

  private formatDate(date: Date | string | null | undefined): string | null {
    if (!date) return null;
    return this.DatePipe.transform(date, 'yyyy-MM-dd');
  }

  uploadFiles() {
    if (this.Files.length >= 15) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload any more files. Have reached max amount of files.'
      );
    }

    const modalRef = this.ModalService.open(ContractFileUploadModalComponent, {
      centered: true,
      size: 'lg'
    });
    modalRef.componentInstance.Files = this.Files || [];
    modalRef.componentInstance.ContractId =
      this.Contract && this.Contract.id ? this.Contract.id : null;
    modalRef.closed.subscribe({
      next: () => {
        this.SharedDataService.sendEvent('UpdateContractFiles', this.Files);
        this._cdr.markForCheck();
      }
    });
  }

  deleteContractFile(index: number, FileId?: number) {
    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', centered: true }
    );
    Object.assign(componentInstance, {
      displayText: 'Are you sure you want to delete contract file?'
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          if (FileId) {
            if (!this.Contract) {
              throw this.ToastService.addErrorToast(
                'Error',
                'No Contract. Cannot delete file.',
                { returnError: true }
              );
            }

            const { id: ContractId } = this.Contract;

            if (ContractId) {
              this.ContractService.deleteContractFile(
                ContractId,
                FileId
              ).subscribe({
                next: () => {
                  this.Files = this.Files.filter((file) => file.id !== FileId);

                  this.ToastService.addSuccessToast(
                    'Success',
                    'Successfully deleted contract file.'
                  );
                  this._cdr.markForCheck();
                }
              });
            }
          } else {
            this.Files.splice(index, 1);
            this._cdr.markForCheck();
          }
        }
      }
    });
  }

  downloadContractDocument(FileId: number) {
    if (!this.Contract) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No Contract Found. Cannot Download.',
        { returnError: true }
      );
    }

    this.ContractService.getContract({
      id: this.Contract.id,
      File: JSON.stringify({
        id: FileId
      })
    }).subscribe({
      next: (Contract: Contract) => {
        if (!Contract) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Contract Found during download attempt.',
            { returnError: true }
          );
        }

        const { id: ContractId, Files } = Contract;

        if (!ContractId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing ContractId.',
            { returnError: true }
          );
        }

        if (!Files || Files.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Files Found for Download.',
            { returnError: true }
          );
        }

        const contractDocument: FileModel = Files[0];
        const { filepath, filename, FileContent } = contractDocument;

        if (filepath) {
          this.ContractService.getContractFileS3PresignedUrl(
            ContractId,
            FileId
          ).subscribe({
            next: ({ presignedUrl, filename }) => {
              saveAs(presignedUrl, filename);
            }
          });
        } else {
          if (!FileContent) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No File Content to Download.',
              { returnError: true }
            );
          }

          const { filecontents } = FileContent;

          if (!filecontents) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No File Contents to Download.',
              { returnError: true }
            );
          }

          const arrayBuffer =
            this.UtilityService.convertBase64StringToArrayBuffer(filecontents);
          const unit8Array = new Uint8Array(arrayBuffer);
          const blob = new Blob([unit8Array]);
          saveAs(blob, filename);
        }
      }
    });
  }

  ngOnInit(): void {
    this.Route.paramMap.subscribe({
      next: (paramMap) => {
        const ContractId = Number(paramMap.get('ContractId'));

        if (ContractId) {
          this.ContractService.getContract(
            this.UtilityService.stringifyObjectsInQueryObject({
              id: ContractId,
              File: {}
            })
          ).subscribe({
            next: (Contract) => {
              if (Contract) {
                this.Contract = Contract;
                const { Files } = this.Contract;

                if (Files && Files.length > 0) {
                  this.Files = Files;
                }

                this.contractForm.patchValue(this.Contract);
                this._cdr.markForCheck();
              }
            }
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.sharedDataServiceSubscription.unsubscribe();
  }
}
